import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { FileCode, Loader2, Search, X } from 'lucide-react';
import type { CascadeGranulation, CascadeStepsConfig, PostWithSlug } from '@/types/price-rules';

/** Pecas comuns aos modais de criar e editar cascata */

export const normalizeSlug = (s: string | null | undefined) => (s || '').replace(/^\/+/, '').replace(/\/+$/, '');

/** Chave (post, ad unit) — post null e a regra global do site */
export const cascadeKey = (post: string | null, unit: string) => `${post || '__global__'}|${unit}`;

export function SectionLabel({ icon, tone, children }: { icon: ReactNode; tone: string; children: ReactNode }) {
    return (
        <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <span className={`p-1 rounded ${tone}`}>{icon}</span>
            {children}
        </span>
    );
}

/**
 * Seletor de granulacao de um ad unit. Com `inheritLabel`, a opcao vazia nao e
 * "Padrao": no Rewarded pos-redirect o script cai na granulacao da origem.
 */
export function GranulationSelect({ value, onChange, adUnit, granulations, cascadeStepsConfig, inheritLabel, disabled, className }: {
    value: number | null;
    onChange: (v: number | null) => void;
    adUnit: string;
    granulations: CascadeGranulation[] | undefined;
    cascadeStepsConfig: CascadeStepsConfig | undefined;
    inheritLabel?: string;
    disabled?: boolean;
    className?: string;
}) {
    return (
        <Select
            value={String(value ?? 'default')}
            onValueChange={(v) => onChange(v === 'default' ? null : Number(v))}
            disabled={disabled}
        >
            <SelectTrigger className={className ?? 'h-8 w-[210px] text-xs'}>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="default">
                    {inheritLabel ?? `Padrão (${cascadeStepsConfig?.[adUnit]?.count ?? '—'} steps)`}
                </SelectItem>
                {granulations?.map(g => (
                    <SelectItem key={g.id} value={String(g.id)}>
                        {g.name} ({g.steps.length} steps)
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

/**
 * Campo de post: ID digitado ou escolhido na lista de posts com trafego no GAM.
 * A lista so aparece enquanto o campo esta vazio, como no formulario antigo.
 */
export function PostPicker({ value, onChange, onRemove, posts, loading, hasDomain, slugOf, takenIds }: {
    value: string;
    onChange: (v: string) => void;
    onRemove?: () => void;
    posts: PostWithSlug[];
    loading: boolean;
    hasDomain: boolean;
    slugOf: (id: string) => string | null;
    takenIds: string[];
}) {
    const [search, setSearch] = useState('');
    const filtered = useMemo(() => {
        if (!search) return posts;
        const q = search.toLowerCase();
        return posts.filter(p => (p.slug && p.slug.toLowerCase().includes(q)) || p.id_post_wp.includes(q));
    }, [posts, search]);
    const slug = value ? slugOf(value) : null;

    return (
        <div className="space-y-1.5">
            <div className="flex items-center gap-2">
                <Input
                    type="text"
                    placeholder="Digite o ID do post manualmente"
                    value={value}
                    onChange={(e) => onChange(e.target.value.trim())}
                    className="flex-1 h-9 text-sm"
                    disabled={!hasDomain}
                />
                {value && (
                    <button type="button" onClick={() => onChange('')} className="text-xs text-gray-500 hover:text-gray-700 font-medium shrink-0">
                        Limpar
                    </button>
                )}
                {onRemove && (
                    <button
                        type="button"
                        onClick={onRemove}
                        className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                        title="Remover este post de origem"
                        aria-label="Remover este post de origem"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {value && (
                slug
                    ? <span className="text-xs text-indigo-600 pl-1">/{slug}</span>
                    : <span className="text-xs text-gray-400 pl-1">a slug será buscada no WordPress ao salvar</span>
            )}

            {!value && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    {!hasDomain ? (
                        <div className="p-4 text-center text-sm text-gray-400">Selecione um domínio para listar os posts</div>
                    ) : loading ? (
                        <div className="flex items-center justify-center p-4 gap-2 text-sm text-gray-500">
                            <Loader2 className="h-4 w-4 animate-spin" /> Buscando posts com tráfego...
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="p-4 text-center text-sm text-gray-400">Nenhum post com tráfego encontrado</div>
                    ) : (
                        <>
                            <div className="p-2 border-b border-gray-100">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Buscar slug..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="w-full h-8 pl-8 pr-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-indigo-500"
                                    />
                                </div>
                            </div>
                            <div className="max-h-[160px] overflow-y-auto">
                                {filtered.map((p) => {
                                    const taken = takenIds.includes(p.id_post_wp);
                                    return (
                                        <button
                                            key={p.id_post_wp}
                                            type="button"
                                            disabled={taken}
                                            onClick={() => onChange(p.id_post_wp)}
                                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 border-b border-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <FileCode className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
                                            <span className="truncate font-medium text-gray-800">{p.slug ? `/${p.slug}` : `Post #${p.id_post_wp}`}</span>
                                            <span className="text-xs text-gray-400 shrink-0">ID: {p.id_post_wp}</span>
                                            <span className="text-xs text-gray-400 shrink-0 ml-auto">
                                                {taken ? 'já escolhido' : `${new Intl.NumberFormat('pt-BR').format(p.impressions)} imp`}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
